/**
 * THE SIGN-IN START NEVER WAITS IN SILENCE (2026-10-03, Justin's stuck "getting your threads back…").
 *
 * Firebase checks its own browser database when it boots. A delete of that database that another prest0 tab started
 * and cannot finish (the tab's own SDK holds it open; an older build's sign-out did that) queues every later open of
 * it, so this page's sign-in start waits until every prest0 tab is closed, and every surface waiting on "who is
 * here?" waits with it. The sign-out wipe no longer deletes these databases (siteDataWipe.ts), but a tab still on an
 * older build can, and nothing on this page can release it.
 *
 * So, from the moment the app boots: if sign-in has not answered after WATCH_AFTER_MS, the watch tries to open
 * Firebase's two databases, each bounded at PROBE_TIMEOUT_MS (an open that cannot happen in that time is queued behind
 * someone's delete; an absent database is never created: the upgrade that would create it is aborted). What it finds
 * is stamped once for the beacon (authStallVital.ts). When a database is held, a notice says which step clears it:
 * close the other prest0 tab or window. It goes away by itself the moment sign-in answers, and offers a reload.
 *
 * Browser build only (started from src/utils/firebase.ts after initializeAuth). The notice is plain DOM, mounted
 * once on document.body at the app notice rung, wearing the portal marker so a click on it never closes the chat.
 */
import { APP_NOTICE_Z_INDEX } from '../../styles/layers';
import { AUTH_STALL_NOTICE_COPY, authStallLang } from './authStallCopy';
import { stampAuthAnswered, stampAuthStall, type AuthStallRung } from './authStallVital';
import { databaseOpensInTime as opensWithin } from './firebaseDatabases';

/** How long sign-in may take to answer before the watch looks. */
export const WATCH_AFTER_MS = 10_000;
/** How long one database open may take before it counts as held. */
export const PROBE_TIMEOUT_MS = 1_500;

/** Firebase's own databases, in the order a held one is reported. */
export const WATCHED_DATABASES: ReadonlyArray<{ name: string; rung: AuthStallRung }> = [
  { name: 'firebaseLocalStorageDb', rung: 'auth-db' },
  { name: 'firebase-heartbeat-database', rung: 'heartbeat-db' },
];

export const AUTH_STALL_NOTICE_ID = 'prest0-auth-stall-notice';

/** Does this database open in time? Never creates it, never holds it (./firebaseDatabases.ts). */
export function databaseOpensInTime(name: string, timeoutMs: number = PROBE_TIMEOUT_MS): Promise<boolean> {
  return opensWithin(name, timeoutMs);
}

export interface AuthStartupWatchDeps {
  /** Subscribe to sign-in's first answer; returns an unsubscribe. */
  onAnswer: (answered: () => void) => () => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  opensInTime?: (name: string) => Promise<boolean>;
  documentRef?: Document;
  language?: string;
  reload?: () => void;
}

export interface AuthStartupWatch {
  /** What the watch found, once it looked (null before, or when sign-in answered first). */
  readonly found: AuthStallRung | null;
  /** Resolves once the watch has nothing more to do (answered, or looked and finished). */
  readonly done: Promise<void>;
  stop(): void;
}

function showNotice(doc: Document, lang: 'en' | 'es', reload: () => void): HTMLElement | null {
  try {
    if (doc.getElementById(AUTH_STALL_NOTICE_ID)) return doc.getElementById(AUTH_STALL_NOTICE_ID);
    const copy = AUTH_STALL_NOTICE_COPY[lang];
    const el = doc.createElement('div');
    el.id = AUTH_STALL_NOTICE_ID;
    el.setAttribute('role', 'status');
    el.setAttribute('data-prest0n-portal', 'true');
    el.setAttribute('data-auth-stall', '');
    el.style.cssText = [
      'position:fixed', 'left:50%', 'bottom:24px', 'transform:translateX(-50%)',
      `z-index:${APP_NOTICE_Z_INDEX}`, 'max-width:min(560px, calc(100vw - 32px))', 'box-sizing:border-box',
      'display:flex', 'align-items:center', 'gap:12px', 'padding:12px 16px', 'border-radius:12px',
      'background:var(--brand-black)', 'color:#ffffff', 'font:14px/1.45 Inter, system-ui, sans-serif',
      'box-shadow:0 8px 24px rgba(0,0,0,0.24)',
    ].join(';');
    const text = doc.createElement('span');
    text.textContent = copy.text;
    const button = doc.createElement('button');
    button.type = 'button';
    button.textContent = copy.reload;
    button.style.cssText = [
      'flex:none', 'min-height:36px', 'padding:6px 14px', 'border-radius:999px', 'border:1px solid #ffffff',
      'background:transparent', 'color:#ffffff', 'font:inherit', 'cursor:pointer',
    ].join(';');
    button.addEventListener('click', () => reload());
    el.appendChild(text);
    el.appendChild(button);
    (doc.body || doc.documentElement).appendChild(el);
    return el;
  } catch {
    return null;
  }
}

export function startAuthStartupWatch(deps: AuthStartupWatchDeps): AuthStartupWatch {
  const doc = deps.documentRef ?? (typeof document !== 'undefined' ? document : undefined);
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => {
    const handle = setTimeout(fn, ms) as unknown as { unref?: () => void };
    try { handle.unref?.(); } catch { /* a browser's timer is a number */ }
    return handle;
  });
  const opensInTime = deps.opensInTime ?? ((name: string) => databaseOpensInTime(name));
  const reload = deps.reload ?? (() => { try { window.location.reload(); } catch { /* nothing to do */ } });
  const lang = authStallLang(deps.language ?? (typeof navigator !== 'undefined' ? navigator.language : 'en'));

  let answered = false;
  let stopped = false;
  let found: AuthStallRung | null = null;
  let notice: HTMLElement | null = null;
  let finish: () => void = () => undefined;
  const done = new Promise<void>((resolve) => { finish = resolve; });

  const removeNotice = () => {
    try { notice?.remove(); } catch { /* already gone */ }
    notice = null;
  };

  let unsubscribe: () => void = () => undefined;
  const onAnswered = () => {
    if (answered) return;
    answered = true;
    stampAuthAnswered();
    removeNotice();
    try { unsubscribe(); } catch { /* already gone */ }
    finish();
  };
  try {
    unsubscribe = deps.onAnswer(onAnswered);
  } catch {
    finish();
  }

  setTimer(() => {
    if (answered || stopped) { finish(); return; }
    void (async () => {
      let rung: AuthStallRung = 'unheld';
      for (const db of WATCHED_DATABASES) {
        let opens = true;
        try { opens = await opensInTime(db.name); } catch { opens = true; }
        if (!opens) { rung = db.rung; break; }
      }
      found = rung;
      stampAuthStall(rung);
      if (rung !== 'unheld' && !answered && !stopped && doc) notice = showNotice(doc, lang, reload);
      if (answered) removeNotice();
      // Still unanswered: the notice stays until sign-in answers (onAnswered removes it and finishes).
      if (answered || rung === 'unheld') finish();
    })();
  }, WATCH_AFTER_MS);

  return {
    get found() { return found; },
    done,
    stop() {
      stopped = true;
      removeNotice();
      try { unsubscribe(); } catch { /* already gone */ }
      finish();
    },
  };
}
